import type { AuthUser } from "@/modules/auth/types";
import { isSubUserAccount } from "@/modules/auth/utils/userAccess";
import type { BookingSelectOption } from "@/modules/booking/services/freeTrial.api";

export type AttendeeOption = BookingSelectOption & { isCurrentUser?: boolean };

/**
 * Sub-users have no independent booking history of their own — they're
 * attendees under a parent account, not standalone accounts. For a
 * switched-into sub-user session (useCurrentUser().user IS the dependant),
 * the attendee list must still surface the parent + siblings, mirroring how
 * modules/auth/utils/userAccess.ts's resolvePrimaryAccount() resolves
 * activation from the primary account rather than the sub-user itself.
 *
 * Shared by FreeTrialBookingContent and CourseBookingContent so both flows
 * resolve attendees identically instead of each re-implementing this.
 */
export function buildAttendeeOptions(
  user: AuthUser | null,
  youLabel: string = "You",
): AttendeeOption[] {
  if (!isRecord(user)) {
    return [];
  }

  const selfId = resolveId(user);
  // Matches userAccess.ts's resolvePrimaryAccount()/isSubUserAccount() sub-user
  // test (parent_id != null) — a stricter typeof-number check here previously
  // meant a parent_id sent as a numeric string misclassified a sub-user as a
  // parent, silently breaking their attendee list.
  const isSubUser = isSubUserAccount(user);

  // ─── PARENT PROFILE ────────────────────────────────────────────────────────
  // Show: parent (self, highlighted) first, then all child accounts.
  if (!isSubUser) {
    const options: AttendeeOption[] = [];

    if (selfId) {
      options.push({
        value: selfId,
        label: `${resolveDisplayName(user)} (${youLabel})`,
        isCurrentUser: true,
      });
    }

    const dependants = Array.isArray(user.dependants) ? user.dependants : [];
    dependants.forEach((dep) => {
      if (!isRecord(dep)) return;
      const id = resolveId(dep);
      if (!id) return;
      options.push({
        value: id,
        label: resolveDisplayName(dep),
        isCurrentUser: false,
      });
    });

    return options;
  }

  // ─── SUB-USER (CHILD) PROFILE ───────────────────────────────────────────────
  // Show: parent first, then all siblings (including self, highlighted).
  // Fallback to self-only if no parent data is available.
  const options: AttendeeOption[] = [];
  const parent = isRecord(user.parent) ? user.parent : null;

  if (parent) {
    const parentId = resolveId(parent);
    if (parentId) {
      options.push({
        value: parentId,
        label: resolveDisplayName(parent),
        isCurrentUser: false,
      });
    }

    const siblings = Array.isArray(parent.dependants) ? parent.dependants : [];
    siblings.forEach((sibling) => {
      if (!isRecord(sibling)) return;
      const sibId = resolveId(sibling);
      if (!sibId) return;

      const isSelf = sibId === selfId;
      options.push({
        value: sibId,
        label: isSelf
          ? `${resolveDisplayName(sibling)} (${youLabel})`
          : resolveDisplayName(sibling),
        isCurrentUser: isSelf,
      });
    });

    if (selfId && !options.some((o) => o.value === selfId)) {
      options.push({
        value: selfId,
        label: `${resolveDisplayName(user)} (${youLabel})`,
        isCurrentUser: true,
      });
    }
  } else if (selfId) {
    options.push({
      value: selfId,
      label: `${resolveDisplayName(user)} (${youLabel})`,
      isCurrentUser: true,
    });
  }

  return options.filter((o) => o.value !== "");
}

function resolveId(value: Record<string, unknown>): string {
  return typeof value.id === "number" ? String(value.id) : "";
}

function resolveDisplayName(value: Record<string, unknown>): string {
  if (typeof value.name === "string" && value.name.trim().length > 0) {
    return value.name.trim();
  }

  const fname = typeof value.fname === "string" ? value.fname.trim() : "";
  const lname = typeof value.lname === "string" ? value.lname.trim() : "";
  const fullName = `${fname} ${lname}`.trim();

  return fullName || "—";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
